import React, {useState, useEffect} from "react";
import { IExtendedL3 } from "../../services/NetworkApi";
import { Table } from "../../UI/Table";
import { TableHeader } from "../TableHeader";
import { Typography } from "../../UI/Typography";
import { COLOR_WHITE } from "../../constants/styles";
import { TableHead } from "../../UI/TableHead";
import { TableRow } from "../../UI/TableRow";
import { TableCell } from "../../UI/TableCell";
import { HelpTooltip } from "../HelpTooltip";
import { EnhancedTableBody } from "../EnhancedTableBody";
import { StatusSwitch } from "./StatusSwitch";
import { SetAllToButtons } from "./SetAllToButtons";
import { VisibilitySwitch } from "./VisibilitySwitch";
import { TablePagination } from "../TablePagination";
import { useTablePaginationContext } from "../TablePagination/context";
import { usePaginationHandler } from "../../hooks/usePaginationHandler";
import { useMatchingRulesHandler } from "../../hooks/useMatchingRulesHandler";

const TOOLTIP_HELP_TITLE = "New to New Matching'";
const TOOLTIP_HELP_CONTENT =
  "This tool restricts new residents from seeing or matching with other new residents. It is meant to prevent you from receiving matches between new residents when there are no available units to place them into. If a new resident is already matched with a renewal resident, we do not classify this group as a new resident group. If you enable or disable new matching for a unit type, it will only affect future users - RoomSync will not break up any existing matches. Even if the new matching is off, you can bypass this and match new residents together using the admin matching tools.";

const TOOLTIP_VISIBLE_HELP_TITLE = "Renewals Visible";
const TOOLTIP_VISIBLE_HELP_CONTENT =
  "If visible, this means that new residents can see other new residents in RoomSync";

interface IProps {
}

export const NewToNewMatchingTable: React.FC<IProps> = (props) => {
  const { data, isLoading, setData, getData } = useMatchingRulesHandler();
  const { page, setPage, perPage, setPages } = useTablePaginationContext();
  const [ filteredData, setFilteredData ] = useState<IExtendedL3[]>([]);
  const [ showArchived, setShowArchived ] = useState<boolean>(false);

  const { selectedRows, pages } = usePaginationHandler(
    filteredData,
    page,
    perPage
  );
  
  useEffect(() => {
    const newData = data.filter(row => showArchived? row : row.matchingRoundStatus!== "ARCHIVED");
    setFilteredData(newData);
  }, [showArchived, data]);

  const handleToggleArchiveChange = (_: any, checked: boolean) =>setShowArchived(checked);

  const isReadOnly = (row: any) => (row?.matchingRoundStatus === "ARCHIVED" || row?.matchingRoundStatus === "CLOSED");

  React.useEffect(() => {
    setPages(pages);
    setPage(1);
  }, [pages.length]);

  return (
    <>
      <TableHeader
        endAdornment={
          <SetAllToButtons
            unitTypes={data}
            updateData={setData}
            getData={getData}
            showArchived={showArchived}
            changeArchivedVisiblity={handleToggleArchiveChange}
          />
        }
      >
        <Typography fontWeight={600} color={COLOR_WHITE}>
          New to New Matching
        </Typography>
      </TableHeader>
      <Table>
        <TableHead secondary>
          <TableRow>
            <TableCell>Matching Round </TableCell>
            <TableCell>
              <HelpTooltip
                title={TOOLTIP_HELP_TITLE}
                text={TOOLTIP_HELP_CONTENT}
              >
                New Resident Matching Allowed
              </HelpTooltip>
            </TableCell>
            <TableCell>
              <HelpTooltip
                title={TOOLTIP_VISIBLE_HELP_TITLE}
                text={TOOLTIP_VISIBLE_HELP_CONTENT}
              >
                New Residents Visible
              </HelpTooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <EnhancedTableBody colSpan={3} isLoading={isLoading}>
          {selectedRows.map((row) => (
            <TableRow key={row.key}>
              <TableCell>
                {row.l2name} | {row.name}
              </TableCell>
              <TableCell>
                <StatusSwitch
                  l2Id={row.l2Id}
                  l3Id={row.l3Id}
                  updateData={setData}
                  defaultValue={row.newNewMatchingAllowed}
                  isDisabled={isReadOnly(row)}
                />
              </TableCell>
              <TableCell>
                <VisibilitySwitch
                  l2Id={row.l2Id}
                  l3Id={row.l3Id}
                  updateData={setData}
                  defaultValue={row.newNewFindingAllowed}
                  isDisabled={Boolean(row.newNewMatchingAllowed) || isReadOnly(row)}
                />
              </TableCell>
            </TableRow>
          ))}
        </EnhancedTableBody>
      </Table>
      <TablePagination />
    </>
  );
};
